import {v4 as uuid} from 'uuid';

class Form {
	constructor(config) {
		this.config = {
			scope: '',
			floating: false,
			label: true,
			...config
		};

		this.forms = document.querySelectorAll(this.config.scope);
		if (this.forms.length > 0) this.init();
	}

	init() {
		const skipGroups = ['form-group-submit', 'form-group-google_recaptcha_v2', 'form-group-google_recaptcha_v3'];

		this.forms.forEach(form => {
			form.dataset.uniqueId = uuid();

			[...form.querySelectorAll('.form-group')].forEach(group => {
				if (skipGroups.some(cls => group.classList.contains(cls))) return;

				if (group.querySelectorAll('input + input').length) return this.makeMultiColumnInput(form, group);
				if (group.classList.contains('form-group-radio')) return this.makeRadioGroup(form, group);
				if (group.classList.contains('form-group-checkbox')) return this.makeCheckboxGroup(form, group);
				if (group.querySelector('[type="file"]')) return this.makeFile(form, group);
				if (group.querySelector('[type="date"]')) return this.makeDatepicker(form, group);
				if (group.querySelector('textarea')) return this.makeTextarea(form, group);
				if (group.querySelector('select')) return this.makeSelect(form, group);
				if (group.querySelector('input')) return this.makeInput(form, group);
			});
		});
	}

	#stripRequired(text) {
		return text ? text.replace(/\s*\*\s*$/, '') : '';
	}

	get #labelClass() {
		return this.config.label ? 'form-label' : 'form-label visually-hidden sr-only';
	}

	#getLabelText(group, input = null) {
		let labelAbove = group.querySelector('.label-above');
		if (labelAbove) return this.#stripRequired(labelAbove.innerHTML);

		if (input?.placeholder) return this.#stripRequired(input.placeholder);

		if (input?.tagName === 'SELECT') {
			let emptyOpt = input.querySelector('option[value=""]');
			if (emptyOpt) return this.#stripRequired(emptyOpt.textContent);
		}

		let firstLabel = group.querySelector(':scope > label');
		if (firstLabel) return this.#stripRequired(firstLabel.textContent);

		return '';
	}

	#buildField(id, label, fieldHtml, canFloat = true) {
		let wrapper = document.createElement('div');
		wrapper.className = 'form-group';

		if (canFloat && this.config.floating) {
			wrapper.innerHTML = `
                <div class="input-group has-validation">
                    <div class="form-floating">
                        ${fieldHtml}
                        <label for="${id}">${label}</label>
                    </div>
                </div>
            `;
		} else {
			wrapper.innerHTML = `
                <label for="${id}" class="${this.#labelClass}">${label}</label>
                <div class="input-group has-validation">
                    ${fieldHtml}
                </div>
            `;
		}

		return wrapper;
	}

	#replaceGroup(group, newEl) {
		group.after(newEl);
		group.remove();
	}

	#makeSimpleField(form, group, selector, canFloat, template) {
		let input = group.querySelector(selector);
		if (!input) return;

		let id = `${form.dataset.uniqueId}-${input.name}`;
		let label = this.#getLabelText(group, input);

		this.#replaceGroup(group, this.#buildField(id, label, template(input, id, label), canFloat));
	}

	#makeOptionGroup(group, type, inlineAmount = 2) {
		let label = this.#getLabelText(group);
		let inputs = group.querySelectorAll(`[type="${type}"]`);
		let inline = Number.isInteger(inlineAmount) && inputs.length <= inlineAmount ? ' form-check-inline' : '';
		let checks = '';

		inputs.forEach(input => {
			let inputLabel = group.querySelector(`label[for="${input.id}"]`)?.textContent || '';

			checks += `
				<div class="form-check${inline}">
					<input class="form-check-input" type="${type}" name="${input.name}" id="${input.id}" value="${input.value}" ${input.required ? 'required' : ''}>
					<label class="form-check-label" for="${input.id}">${inputLabel}</label>
				</div>
			`;
		});

		let fieldset = document.createElement('fieldset');
		fieldset.className = 'form-group';
		fieldset.innerHTML = `<legend class="${this.#labelClass}">${label}</legend>${checks}`;


		this.#replaceGroup(group, fieldset);
	}

	makeMultiColumnInput(form, group) {
		let inputs = group.querySelectorAll('input');
		let colClass = inputs.length === 2 ? 'col-md-6' : 'col-md-4';
		let floating = this.config.floating;
		let fields = '';

		inputs.forEach(input => {
			let id = `${form.dataset.uniqueId}-${input.name}`;
			let label = input.placeholder || input.name;
			let required = input.required ? 'required' : '';

			if (floating) {
				fields += `
					<div class="col-12 ${colClass}">
						<div class="input-group has-validation">
							<div class="form-floating">
								<input type="${input.type}" name="${input.name}" class="form-control" id="${id}" placeholder=" " ${required}>
								<label for="${id}">${label}</label>
							</div>
						</div>
					</div>
				`;
			} else {
				fields += `
                    <div class="col-12 ${colClass}">
                        <label for="${id}" class="${this.#labelClass}">${label}</label>
                        <div class="input-group has-validation">
                            <input type="${input.type}" name="${input.name}" class="form-control" id="${id}" placeholder="${label}" ${required}>
                        </div>
                    </div>
                `;
			}
		});

		let row = document.createElement('div');
		row.className = 'form-group row g-3';
		row.innerHTML = fields;

		this.#replaceGroup(group, row);
	}

	makeInput(form, group) {
		let floating = this.config.floating;

		this.#makeSimpleField(form, group, '[type="text"], [type="email"], [type="number"], [type="password"]', true,
			(input, id, label) => {
				let placeholder = floating ? ' ' : label;
				let autocomplete = {password: 'current-password', email: 'email'}[input.type] || '';
				return `<input type="${input.type}" name="${input.name}" class="form-control" id="${id}" placeholder="${placeholder}" ${autocomplete ? `autocomplete="${autocomplete}"` : ''} ${input.required ? 'required' : ''}>`;
			}
		);
	}

	makeTextarea(form, group) {
		let input = group.querySelector('textarea');
		let id = `${form.dataset.uniqueId}-${input.name}`;
		let label = this.#getLabelText(group, input);

		let placeholderEl = form.closest('[data-textarea-placeholder]');
		let dataPlaceholder = placeholderEl?.dataset.textareaPlaceholder || '';

		let displayLabel = this.config.floating ? (dataPlaceholder || label) : label;
		let placeholder = dataPlaceholder
			|| (this.config.floating ? ' ' : (!this.config.label ? label : ''));

		this.#replaceGroup(group, this.#buildField(id, displayLabel,
			`<textarea class="form-control" id="${id}" name="${input.name}" placeholder="${placeholder}" ${input.required ? 'required' : ''}></textarea>`,
			true
		));
	}

	makeSelect(form, group) {
		let showLabel = this.config.label || this.config.floating;

		this.#makeSimpleField(form, group, 'select', true,
			(input, id, label) => {
				let options = [...input.querySelectorAll('option')].map(opt => {
					if (opt.value === '') {
						if (showLabel) return '';
						return `<option value="" selected disabled>${opt.textContent}</option>`;
					}
					return opt.outerHTML;
				}).join('');

				return `<select class="form-select" id="${id}" name="${input.name}" aria-label="${label}" ${input.required ? 'required' : ''}>${options}</select>`;
			}
		);
	}

	makeDatepicker(form, group) {
		this.#makeSimpleField(form, group, '[type="date"]', false,
			(input, id) => `<input type="date" name="${input.name}" class="form-control" id="${id}" ${input.required ? 'required' : ''}>`
		);
	}

	makeFile(form, group) {
		this.#makeSimpleField(form, group, '[type="file"]', false,
			(input, id) => `<input type="file" name="${input.name}" class="form-control" id="${id}" ${input.required ? 'required' : ''}>`
		);
	}

	makeRadioGroup(form, group) {
		this.#makeOptionGroup(group, 'radio', 4);
	}

	makeCheckboxGroup(form, group) {
		this.#makeOptionGroup(group, 'checkbox', 2);
	}
}

export default Form;
